import { ProjektListenTyp, SpsListenTyp } from './sps-listen-typ.model';
import { ExcelAiAnalysis } from '../messstelle.model';
import { SpsFortschritt, SpsPruefstatus, SpsSignalRichtung, SpsZuordnungsStatus } from './sps-signal.model';

/*
 * Spiegelbild der Backend-DTOs aus Models_SpsDtos.cs (Abschnitte
 * Listenerkennung, SPS-Importvorschau, SPS-Listen). Enums kommen als Klartext.
 * Die KI-Analyse (`ExcelAiAnalysis`) stammt aus dem bestehenden Import und
 * wird unverändert weiterverwendet.
 */

// =========================================================
// LISTENERKENNUNG  (POST api/sps/import/erkennen)
// =========================================================

export interface ListenErkennungBlatt {
  blatt: string;
  typ: ProjektListenTyp;
  sicherheit: number;
  kopfzeile: number | null;
  datenzeilen: number;
  gefundeneMerkmale: string[];
}

export interface ListenErkennung {
  dateiName: string;
  /** Bester Treffer über alle Blätter, 'Unbekannt' wenn nichts passt. */
  typ: ProjektListenTyp;
  /** 0..1 – unter 0,6 sollte der Benutzer den Typ bestätigen. */
  sicherheit: number;
  eindeutig: boolean;
  blatt: string | null;
  kopfzeile: number | null;
  gefundeneMerkmale: string[];
  blaetter: ListenErkennungBlatt[];
  hinweise: string[];
  /** True, wenn für Messstellen-/Verbraucherlisten die bestehende KI befragt wurde. */
  kiVerwendet: boolean;
  /** KI-Ergebnis (bestehendes Modell `ExcelAiAnalysis`), nur bei kiVerwendet. */
  kiAnalyse: ExcelAiAnalysis | null;
}


// =========================================================
// IMPORTVORSCHAU  (POST api/sps/import/vorschau)
// =========================================================

export interface SpsImportSpalte {
  /** Fachliches Feld, z. B. "Adresse" oder "Logical Address". */
  feld: string;
  /** Originalüberschrift in der Datei, null wenn nicht gefunden. */
  excelSpalte: string | null;
  /** 0-basierter Spaltenindex, -1 wenn nicht gefunden. */
  index: number;
  pflicht: boolean;
  gefunden: boolean;
}

export interface SpsImportVorschauZeile {
  excelZeile: number;
  signalname: string | null;
  adresseOriginal: string | null;
  adresseNormiert: string | null;
  richtung: SpsSignalRichtung;
  stationsName: string | null;
  bmk: string | null;
  beschreibung: string | null;
  datenTyp: string | null;
  gueltig: boolean;
  hinweis: string | null;
}

export interface SpsImportVorschau {
  /** Der gewählte/bestätigte Typ, nach dem geparst wurde. */
  listenTyp: ProjektListenTyp;
  erkannterTyp: ProjektListenTyp;
  erkennungSicherheit: number;
  /** False, wenn Pflichtspalten fehlen – dann ist kein Import möglich. */
  typPasst: boolean;
  dateiName: string;
  blatt: string | null;
  kopfzeile: number;
  blaetter: string[];
  alleSpalten: string[];
  spalten: SpsImportSpalte[];
  zeilenGesamt: number;
  gueltig: number;
  ungueltig: number;
  eingaenge: number;
  ausgaenge: number;
  richtungUnbekannt: number;
  ohneAdresse: number;
  adresseNichtInterpretierbar: number;
  warnungen: string[];
  vorschau: SpsImportVorschauZeile[];
  nichtGezeigteZeilen: number;
  seite: number;
  seitengroesse: number;
  seitenGesamt: number;
  vorschauZeilenGesamt: number;
  /** Datenzeilen auf den Seiten vor bzw. nach dieser Seite – keine fehlenden Datensätze. */
  zeilenVorSeite: number;
  zeilenNachSeite: number;
  /** Laufende Nummern (1-basiert) der ersten/letzten Zeile dieser Seite; 0 bei leerer Seite. */
  ersteZeileAufSeite: number;
  letzteZeileAufSeite: number;

  /** Anlagenteilspalte, die diese Datei liefern kann: 'Ort' (Zuordnungsliste) | 'Path' (PLC-Tags) | null. */
  anlagenteilSpalteVerfuegbar?: SpsAnlagenteilSpalte | null;
  /** Originalüberschrift der Anlagenteilspalte in der Datei. */
  anlagenteilExcelSpalte?: string | null;
  /** Erkannte Anlagenteile (nur mit projektId gegen das Projekt abgeglichen). */
  anlagenteile?: SpsAnlagenteilVorschau | null;
}


// =========================================================
// ANLAGENTEILE AUS SPS-LISTEN (Ort bzw. Path)
// =========================================================

/** Spalte, aus der Anlagenteile übernommen werden – exakt der Zellwert, nicht zerlegt. */
export type SpsAnlagenteilSpalte = 'Ort' | 'Path';

export interface SpsAnlagenteilWert {
  wert: string;
  /** Zeilen der Datei mit diesem Wert. */
  anzahl: number;
  /** True, wenn das Projekt bereits einen Anlagenteil dieses Namens hat (wird wiederverwendet). */
  vorhanden: boolean;
  anlagenteilId: number | null;
}

export interface SpsAnlagenteilVorschau {
  spalte: SpsAnlagenteilSpalte;
  werte: SpsAnlagenteilWert[];
  neu: number;
  vorhanden: number;
  eintraegeMitWert: number;
  eintraegeOhneWert: number;
}

/** Anzeige einer Anlagenteilquelle im Importdialog. */
export function anlagenteilSpalteText(spalte: SpsAnlagenteilSpalte | null | undefined): string {
  switch (spalte) {
    case 'Ort': return 'SPS-Zuordnungsliste – Spalte „Ort“';
    case 'Path': return 'PLC-Tag-Liste – Spalte „Path“';
    default: return 'Keine Anlagenteile übernehmen';
  }
}


// =========================================================
// SPS-LISTEN  (GET api/sps/listen…)
// =========================================================

export interface SpsImportListe {
  id: number;
  projektId: number;
  typ: SpsListenTyp;
  listenTyp: ProjektListenTyp;
  name: string;
  dateiName: string;
  tabellenblatt: string | null;
  importiertAmUtc: string;
  importiertVon: string | null;
  eintraegeAnzahl: number;
  signaleBestaetigt: number;
  signaleUngeklaert: number;
  /** Spalte der Anlagenteile dieser Liste ('Ort' | 'Path'), null = keine. */
  anlagenteilSpalte?: SpsAnlagenteilSpalte | null;
  /** Letztes Listenupdate mit einer neuen Excel-Version; null = nie aktualisiert. */
  aktualisiertAmUtc?: string | null;
  aktualisiertVon?: string | null;
  aktualisierungen?: number;
  /** Zeilen, die bewusst nur aus der SPS-Prüfung entfernt wurden (bleiben in der Liste). */
  eintraegeAusPruefungEntfernt?: number;
}

export interface SpsImportListeDetail extends SpsImportListe {
  /** Spaltenreihenfolge der Originaldaten (aus den ersten Zeilen). */
  spalten: string[];
}

export interface SpsImportEintrag {
  id: number;
  excelZeile: number;
  signalname: string | null;
  symbol: string | null;
  adresseOriginal: string | null;
  stationsName: string | null;
  bmk: string | null;
  anschluss: string | null;
  kanal: string | null;
  beschreibung: string | null;
  datenTyp: string | null;
  ort: string | null;
  platzierung: string | null;
  /** Alle Excel-Spalten der Originalzeile: Überschrift → Wert. */
  originalDaten: Record<string, string>;
  /** Verknüpftes gemeinsames Signal – null, wenn (noch) keines. */
  signalId: number | null;
  zuordnungsStatus: SpsZuordnungsStatus | null;
  zuordnungsGrund: string | null;
  pruefstatus: SpsPruefstatus | null;
  richtung: SpsSignalRichtung | null;
  adresseNormiert: string | null;
  /**
   * Anlagenteil der Zeile (bestehende Anlagenteilverwaltung): aus der Anlagenteilspalte
   * DIESER Liste oder – ohne eigenen – der eindeutige Anlagenteil der anderen bestätigten
   * Quellen desselben Signals (`anlagenteilUebernommen`).
   */
  anlagenteilId?: number | null;
  anlagenteilName?: string | null;
  /** True, wenn der Anlagenteil über das gemeinsame Signal aus einer anderen Liste stammt (nur Anzeige). */
  anlagenteilUebernommen?: boolean;
  /** Gesetzt, wenn die Zeile bewusst nur aus der SPS-Prüfung entfernt wurde (kein Signal, bleibt in der Liste). */
  ausPruefungEntferntAmUtc?: string | null;
  ausPruefungEntferntVon?: string | null;
}

/** Seitenweise Antwort (Signale, Einträge). */
export interface SpsSeite<T> {
  gesamt: number;
  seite: number;
  groesse: number;
  eintraege: T[];
}

/** Was das Löschen einer Liste bewirken würde (GET api/sps/listen/{id}/loeschvorschau) – ändert nichts. */
export interface SpsListeLoeschvorschau {
  listeId: number;
  projektId: number;
  name: string;
  dateiName: string;
  listenTyp: ProjektListenTyp;
  /** Originalzeilen – werden alle entfernt. */
  eintraege: number;
  /** Signale mit einer Quelle aus dieser Liste. */
  signaleBetroffen: number;
  /** Davon mit weiteren Quellen – bleiben samt Prüfung bestehen. */
  signaleMitWeiterenQuellen: number;
  /** Signale ausschließlich aus dieser Liste. */
  signaleNurDieseListe: number;
  /** Davon ungeprüft – werden entfernt. */
  signaleNurDieseListeUngeprueft: number;
  /** Davon geprüft – verschwinden aus der Prüfung, Verlauf bleibt archiviert. */
  signaleNurDieseListeGeprueft: number;
}

/** Ergebnis von DELETE api/sps/listen/{id}. */
export interface SpsListeLoeschErgebnis {
  listeId: number;
  projektId: number;
  name: string;
  eintraegeGeloescht: number;
  quellenGeloescht: number;
  signaleErhalten: number;
  signaleGeloescht: number;
  signaleArchiviert: number;
  signaleAngepasst: number;
  fortschritt: SpsFortschritt;
}


// =========================================================
// IMPORTERGEBNIS  (POST api/sps/import, POST api/sps/import/mehrfach)
// =========================================================

/**
 * Ergebnis eines Imports NACH der automatischen Zusammenführung, die Teil
 * jedes Imports ist: `signaleNeu` sind Signale nur aus dieser Liste,
 * `signaleVerknuepft` Zeilen, die in einem Signal mit weiteren Quellen
 * aufgegangen sind. Der Benutzer muss danach nichts mehr auslösen.
 */
export interface SpsImportErgebnis {
  liste: SpsImportListe;
  eintraegeGespeichert: number;
  eintraegeUebersprungen: number;
  signaleNeu: number;
  signaleVerknuepft: number;
  signaleUngeklaert: number;
  /** Signale, die durch die automatische Zusammenführung dieses Imports in einem anderen aufgegangen sind (auch Altduplikate). */
  automatischZusammengefuehrt: number;
  /** Bisher ungeklärte Zuordnungen, die nach diesem Import konkurrenzlos waren und bestätigt wurden. */
  zuordnungenGeklaert: number;
  /** Fälle im Projekt, die weiterhin eine manuelle Entscheidung verlangen. */
  verbleibendeKonflikte: number;
  /** Prüfbare Signale des Projekts nach dem Import – dieselbe Zahl wie in der SPS-Prüfung. */
  signaleGesamtProjekt: number;
  /** Anlagenteile aus Ort bzw. Path: neu angelegt / vorhandene wiederverwendet / zugeordnete Zeilen. */
  anlagenteileNeu?: number;
  anlagenteileWiederverwendet?: number;
  eintraegeMitAnlagenteil?: number;
  warnungen: string[];
}

export interface SpsSammelImportDateiErgebnis {
  index: number;
  dateiName: string;
  listenTyp: ProjektListenTyp;
  erfolg: boolean;
  fehler: string | null;
  ergebnis: SpsImportErgebnis | null;
}

export interface SpsSammelImportErgebnis {
  projektId: number;
  dateien: SpsSammelImportDateiErgebnis[];
  erfolgreich: number;
  fehlgeschlagen: number;
  /** Jede Datei hat ihre eigene Transaktion – ein Fehler macht die anderen nicht rückgängig. */
  transaktionshinweis: string;
}

/** Eine für den Sammelimport vorbereitete Datei (clientseitiger Arbeitsstand). */
export interface SpsSammelImportDatei {
  file: File;
  listenTyp: ProjektListenTyp;
  sheetName?: string | null;
  name?: string | null;
  /** 'Ort' (Zuordnungsliste) oder 'Path' (PLC-Tags): Anlagenteile aus dieser Datei übernehmen. */
  anlagenteilSpalte?: SpsAnlagenteilSpalte | null;
}
